import React, { FC, MouseEvent } from 'react';
import { connect } from 'react-redux';
import qs from 'qs';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { FR_SP3 } from '@shared/styles/vars';
import { Label } from '@src/components/Label';
import { trackEvent } from '@src/metrics';
import { setModalContent } from '@src/redux/actions/modal';
import { recordSignals } from '@src/graphql/signals/actions';
import { checkSubscriptionState } from '@src/redux/actions/subscription';
import { Avatar } from '@src/components/Avatar';
import { VIEW_VERIFIED_USER_QUERY_PARAM } from '@src/constants';
import { CHATS_ROUTE } from '@src/Routes/links';
import { SignalTypeEnum, SignalEntityTypeEnum, LinkTypeEnum } from '@src/graphql/generated';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { createLink } from '@src/util/createLink';
import { relativeTime } from '@src/util/relativeTime';
import { getNameInitials } from '@src/util/getNameInitials';
import { Actions } from '../Actions';
import { InviteToInterview } from '../InviteToInterview';
import { ActionTypes } from '../Actions/enums/actionTypes.enum';
import { Props } from './props';
import './styles.scss';

const _ActivityFeedItem: FC<Props> = ({
  talentData,
  setModalContent,
  onMessaged,
  wrapperClassName,
  urlLocation,
  modalOptions,
  onOpeningPositionClick,
}) => {
  const history = useHistory();

  const { talent, signal } = talentData;

  const {
    user,
    userVerification,
    messageThread,
    recruiterState: { hasMessaged: hasRecruiterMessaged, hasSeen: hasRecruiterSeen },
  } = talent;

  const { type: signalType, createdAt, entity: signalEntity } = signal;

  const { id: userId, profile, name: userName, position, avatarColor, avatar } = user;

  const {
    location: { shortName: location },
    isRemote,
    isRelocation,
    salary: userSalary,
  } = profile;

  const userVerificationId = userVerification?.id;
  const positionExpectations = userVerification?.positionExpectations;
  const messageThreadId = messageThread?.id;

  const skills = !isEmpty(profile.skills) && profile.skills.map(skill => skill.name).join(', ');
  const lastWorkPlaceCompanyName =
    !isEmpty(profile.workHistory) && !!profile.workHistory[0].endsAt && profile.workHistory[0].companyName;

  const openingTitle = signalEntity?.title;
  const isVerified = !!userVerification;
  const isSmallCard = !isRemote && !isRelocation && !isVerified;

  const existingParams = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });
  const updatedParams = qs.stringify({ ...existingParams, [VIEW_VERIFIED_USER_QUERY_PARAM]: userId });
  const stringifyVtId = qs.stringify({ [VIEW_VERIFIED_USER_QUERY_PARAM]: userId });

  const copyLink = !urlLocation.search
    ? `${window.location.href}?${updatedParams}`
    : `${window.location.href}&${stringifyVtId}`;

  const recordDetailsViewedSignal = () => {
    recordSignals({
      items: [{ id: userVerificationId, type: SignalEntityTypeEnum.USER_VERIFICATION }],
      signalType: SignalTypeEnum.VIEW_USER_VERIFICATION,
    });
  };

  const handleSignalRecordOnThirdPartyLinkClick = (e: MouseEvent, type: LinkTypeEnum) => {
    e.stopPropagation();

    if (type === LinkTypeEnum.LINKEDIN) {
      trackEvent({
        context: MetricContext.TALENT_AGENT_FEED,
        interaction: MetricAction.CLICK_RECOMMENDED_USER_LINKEDIN_ICON,
        payload: {
          'User Id': userId,
        },
      });
    }
  };

  const startInvite = () => {
    trackEvent({
      context: MetricContext.TALENT_AGENT_FEED,
      interaction: MetricAction.VIEW_RECOMMENDED_USER_DETAILS,
      payload: {
        'User Id': userId,
      },
    });

    if (isVerified) {
      recordDetailsViewedSignal();
    }

    setModalContent({
      className: 'vt-invite-modal-wrapper',
      config: {
        withoutPadding: true,
      },
      content: <InviteToInterview modalOptions={modalOptions} onMessaged={onMessaged} userId={talent.user.id} />,
      doNotCloseOnLocationChange: true,
    });

    history.replace({
      pathname: urlLocation.pathname,
      search: updatedParams,
    });
  };

  const handleMessageClick = (event: MouseEvent) => {
    if (hasRecruiterMessaged) {
      event.stopPropagation();
      history.push(
        createLink({
          link: CHATS_ROUTE,
          queryParams: {
            threadId: messageThreadId,
          },
        }),
      );
    }
  };

  const handleCardClick = () => {
    if (isVerified) {
      startInvite();
    }
  };

  const getTitle = () => {
    if (signalType === SignalTypeEnum.START_APPLY) {
      return (
        <>
          <span className="activity-feed-item__header-user-name">{isVerified ? userName : position}</span> started an
          application your{' '}
          <span
            className="activity-feed-item__header-opening-title"
            onClick={e => {
              e.stopPropagation();
              onOpeningPositionClick();
            }}
          >
            {openingTitle}
          </span>{' '}
          offer
        </>
      );
    } else if (signalType === SignalTypeEnum.VIEW_DETAILS) {
      return (
        <>
          <span className="activity-feed-item__header-user-name">{isVerified ? userName : position}</span> viewed your{' '}
          <span
            className="activity-feed-item__header-opening-title"
            onClick={e => {
              e.stopPropagation();
              onOpeningPositionClick();
            }}
          >
            {openingTitle}
          </span>{' '}
          offer
        </>
      );
    }
  };

  return (
    <div className={classnames('activity-feed-item', wrapperClassName)} onClick={handleCardClick}>
      <span className="activity-feed-item__line">
        <span
          className={classnames('activity-feed-item__status', !hasRecruiterSeen && 'activity-feed-item__status--new')}
        />
      </span>
      <div className="activity-feed-item__header">
        <div className="activity-feed-item__header-title">{getTitle()}</div>
        <p className="activity-feed-item__timestamp">{relativeTime(createdAt, false)}</p>
      </div>
      <div className={classnames('activity-feed-item__card', isSmallCard && 'activity-feed-item__card--small')}>
        <div className="activity-feed-item__preview">
          <div className="activity-feed-item__section-one">
            <div className="activity-feed-item__avatar-container">
              <div>
                <Avatar
                  avatarColor={isVerified ? avatarColor : FR_SP3}
                  className="activity-feed-item__avatar"
                  initials={isVerified ? getNameInitials(userName) : 'A'}
                  source={isVerified ? avatar?.fullUrl : undefined}
                />
              </div>

              <div className="activity-feed-item__avatar-info">
                <h3>{isVerified ? userName : 'Anonymous'}</h3>
                <p>{position}</p>
                <p>
                  {lastWorkPlaceCompanyName && `${lastWorkPlaceCompanyName} • `} {location}
                </p>
              </div>
            </div>
            <div className="activity-feed-item__labels">
              {isRelocation && (
                <Label text="RELOCATION" type="invisible" wrapperClassName="activity-feed-item__label" />
              )}

              {isRemote && <Label text="REMOTE" type="invisible" wrapperClassName="activity-feed-item__label" />}
            </div>
          </div>

          <div className="activity-feed-item__section-two">
            <div className="activity-feed-item__salary-exp">
              <h3>Expected Salary</h3>
              <p>{`${userSalary.value}${userSalary.currency.symbol} / ${userSalary.period}`}</p>
            </div>
            {isVerified && (
              <div className="activity-feed-item__speciality">
                <h3>Skills</h3>
                <p>{skills}</p>
              </div>
            )}
          </div>
          <div className="activity-feed-item__section-three">
            {isVerified ? (
              <>
                <h3>Expectations for next position</h3>
                <p>
                  {positionExpectations && positionExpectations.length > 100
                    ? `${positionExpectations.slice(0, 100)}…`
                    : positionExpectations}
                </p>
              </>
            ) : (
              <>
                <h3>Skills</h3>
                <p>{skills}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="activity-feed-item__actions">
        <Actions
          copyText={copyLink}
          hiddenActions={[ActionTypes.SAVE]}
          isUserVerified={isVerified}
          onLinkedInClick={handleSignalRecordOnThirdPartyLinkClick}
          onMessageClick={handleMessageClick}
          verifiedUser={talent}
        />
      </div>
    </div>
  );
};

export const ActivityFeedItem = connect(undefined, {
  checkSubscriptionState,
  setModalContent,
})(_ActivityFeedItem);
